import { memo, useCallback, useMemo } from 'react';
import Badge from './Badge.jsx';
import { Zap } from 'lucide-react';

const variantTokens = {
  green: {
    container: 'border-[#2dd4bf59] bg-[#2dd4bf14]',
    iconWrap: 'bg-[#2dd4bf26]',
    iconColor: 'text-[#2dd4bf]',
    indicatorActive: 'border-[#2dd4bf] bg-[#2dd4bf]',
    indicatorIdle: 'border-[#2dd4bf] bg-transparent',
    badgeVariant: 'success',
  },
  purple: {
    container: 'border-[#a78bfa59] bg-[#a78bfa14]',
    iconWrap: 'bg-[#a78bfa26]',
    iconColor: 'text-[#a78bfa]',
    indicatorActive: 'border-[#a78bfa] bg-[#a78bfa]',
    indicatorIdle: 'border-[#a78bfa] bg-transparent',
    badgeVariant: 'purple',
  },
  lightBlue: {
    container: 'border-[#63ddff] bg-[#e8f8ff] dark:border-[#63ddff] dark:bg-[#244b57]',
    iconWrap: 'bg-[#63ddff33] dark:bg-[#63ddff26]',
    iconColor: 'text-[#0b8fb3] dark:text-[#63ddff]',
    indicatorActive: 'border-[#20bde6] bg-[#20bde6] dark:border-[#63ddff] dark:bg-[#63ddff]',
    indicatorIdle: 'border-[#20bde6] bg-transparent dark:border-[#63ddff] dark:bg-transparent',
    badgeVariant: 'lightBlue',
  },
};

/**
 * Flash-like selectable card with icon, title, badge tag and description.
 *
 * @param {object} props
 * @param {'green'|'purple'|'lightBlue'} [props.variant='green']
 * @param {string} [props.title='']
 * @param {string} [props.description='']
 * @param {string} [props.tagText='']
 * @param {import('react').ElementType} [props.tagIcon] - Optional icon on the left side of badge text.
 * @param {string} [props.tagIconClassName] - Optional classes for badge icon (e.g. color).
 * @param {import('react').ElementType} [props.icon=Zap] - Main icon (left block).
 * @param {import('react').ElementType} [props.titleLeftIcon] - Small optional icon before title.
 * @param {boolean} [props.selected=true]
 * @param {boolean} [props.disabled=false]
 * @param {(nextSelected: boolean, event: import('react').MouseEvent<HTMLButtonElement>) => void} [props.onSelect]
 * @param {(event: import('react').MouseEvent<HTMLButtonElement>) => void} [props.onClick]
 * @param {string} [props.className]
 * @param {string} [props.tagClassName]
 * @param {string} [props.descriptionClassName]
 */
const FlashCard = ({
  variant = 'green',
  title = '',
  description = '',
  tagText = '',
  tagIcon: TagIcon,
  tagIconClassName = '',
  icon: Icon = Zap,
  titleLeftIcon: TitleLeftIcon,
  selected = true,
  disabled = false,
  onSelect,
  onClick,
  className = '',
  tagClassName = '',
  descriptionClassName = '',
}) => {
  const tokens = variantTokens[variant] || variantTokens.green;
  const indicatorClass = useMemo(
    () => (selected ? tokens.indicatorActive : tokens.indicatorIdle),
    [selected, tokens.indicatorActive, tokens.indicatorIdle],
  );

  const handleClick = useCallback(
    (event) => {
      if (disabled) return;
      onClick?.(event);
      onSelect?.(!selected, event);
    },
    [disabled, onClick, onSelect, selected],
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={[
        'w-full rounded-xl border p-3 text-left transition-all',
        'flex items-start gap-3',
        tokens.container,
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        className={[
          'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
          tokens.iconWrap,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <Icon size={14} className={tokens.iconColor} aria-hidden />
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-2">
          {TitleLeftIcon ? (
            <TitleLeftIcon size={11} className="shrink-0 text-content-secondary" aria-hidden />
          ) : null}
          <p className="text-xs font-semibold text-content">{title}</p>
          {tagText ? (
            <Badge
              size="small"
              variant={tokens.badgeVariant}
              icon={TagIcon}
              iconClassName={tagIconClassName}
              className={['!rounded-full !px-1.5 !py-0.5 !text-[9px] !font-bold', tagClassName]
                .filter(Boolean)
                .join(' ')}
            >
              {tagText}
            </Badge>
          ) : null}
        </div>

        <p
          className={['text-[11px] leading-snug text-content-secondary/80', descriptionClassName]
            .filter(Boolean)
            .join(' ')}
        >
          {description}
        </p>
      </div>

      <div
        className={[
          'mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
          indicatorClass,
        ]
          .filter(Boolean)
          .join(' ')}
        aria-hidden
      >
        {selected ? <span className="h-1.5 w-1.5 rounded-full bg-[#131825]" /> : null}
      </div>
    </button>
  );
};

export default memo(FlashCard);
